import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plug, CheckCircle2, Tag, FileText, Save, AlertTriangle, Mail, RefreshCw, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/common";
import TwoFASettings from "@/components/TwoFASettings";
import { useI18n } from "@/context/I18nContext";
import { emailStatusKey } from "@/lib/emailStatus";

const inp = "w-full bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]";
const F = ({ label, value, onChange, wide, placeholder, testid }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className="text-xs text-gray-400 mb-1 block">{label}</label>
    <input className={inp} data-testid={testid} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  </div>
);

function EmailStatusCard() {
  const { t } = useI18n();
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const check = () => {
    setLoading(true);
    api.get("/admin/email-status").then((r) => setSt(r.data))
      .catch(() => setSt({ status: "error", detail: "Prüfung fehlgeschlagen." }))
      .finally(() => setLoading(false));
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email-test");
      if (data.email_sent) toast.success(t("Test-E-Mail an {email} gesendet — bitte Posteingang prüfen.").replace("{email}", data.to));
      else toast.error(t("Test-E-Mail konnte nicht zugestellt werden — Versand aktuell blockiert."));
    } catch (e) { toast.error(e.response?.data?.detail || t("Test-E-Mail fehlgeschlagen.")); }
    finally { setTesting(false); }
  };
  useEffect(() => { check(); }, []);

  const map = {
    enabled: { label: t("Brevo verbunden · allgemeiner Versand freigegeben"), cls: "border-[#A3E635]/40 bg-[#A3E635]/10 text-[#A3E635]", Icon: CheckCircle2 },
    disabled: { label: t("Brevo verbunden · allgemeiner Versand deaktiviert"), cls: "border-white/15 bg-white/5 text-gray-400", Icon: Mail },
    unconfirmed: { label: t("Brevo verbunden · Versandfreigabe unbekannt"), cls: "border-amber-400/40 bg-amber-400/10 text-amber-300", Icon: AlertTriangle },
    ip_blocked: { label: t("Blockiert · IP nicht freigegeben"), cls: "border-amber-400/40 bg-amber-400/10 text-amber-300", Icon: ShieldAlert },
    unauthorized: { label: t("Nicht autorisiert"), cls: "border-red-500/40 bg-red-500/10 text-red-300", Icon: AlertTriangle },
    not_configured: { label: t("Nicht konfiguriert"), cls: "border-white/15 bg-white/5 text-gray-400", Icon: AlertTriangle },
    error: { label: t("Status unbekannt"), cls: "border-red-500/40 bg-red-500/10 text-red-300", Icon: AlertTriangle },
  };
  const m = map[emailStatusKey(st)] || map.error;

  return (
    <Card className="p-6 mb-5" data-testid="email-status-card">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 font-bold"><Mail className="w-5 h-5 text-[#A3E635]" /> {t("E-Mail-Versand (Brevo)")}</div>
        <div className="flex items-center gap-2">
          {st && <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${m.cls}`} data-testid="email-status-badge"><m.Icon className="w-3.5 h-3.5" /> {m.label}</span>}
          <button onClick={sendTest} disabled={testing} data-testid="email-test-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-gray-200 disabled:opacity-60 transition-colors">
            {testing ? t("Sende …") : t("Test-E-Mail senden")}
          </button>
          <button onClick={check} disabled={loading} data-testid="email-status-recheck" title={t("Erneut prüfen")} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      <div className="text-sm text-gray-400 space-y-1">
        <div>{t("Absenderadresse")}: <span className="text-gray-200">{st?.sender || "—"}</span></div>
        {st?.status === "active" && <>
          <div>{t("Zustellung mit einer freigegebenen Test-E-Mail prüfen.")}</div>
          <div className="text-xs text-gray-500">{t("Einzelne freigegebene E-Mail-Typen können separat aktiv sein.")}</div>
        </>}
        {st?.status === "ip_blocked" && (
          <div className="mt-2 flex items-start gap-2 text-amber-200/90 bg-amber-400/10 border border-amber-400/25 rounded-xl p-3" data-testid="email-status-ipblock">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              {t("Brevo blockiert die aktuelle Server-IP.")} {st.egress_ip ? <span>{t("IP")}: <code className="font-mono text-amber-100">{st.egress_ip}</code></span> : null}
              <div className="text-xs text-amber-200/70 mt-1">{t("Lösung: In Brevo unter «Security → Authorised IPs» diese IP freigeben — oder die IP-Beschränkung deaktivieren (empfohlen, da sich die Server-IP ändern kann).")}</div>
            </div>
          </div>
        )}
        {(st?.status === "unauthorized" || st?.status === "not_configured" || st?.status === "error") && st?.detail && (
          <div className="text-xs text-gray-500 mt-1">{st.detail}</div>
        )}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => { setS(r.data); setF(r.data); }); }, []);
  if (!s || !f) return <Spinner />;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSender = (k, v) => setF((p) => ({ ...p, sender: { ...(p.sender || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        brand: f.brand, legal_company: f.legal_company, sender_line: f.sender_line,
        sender: f.sender, bexio_account_name: f.bexio_account_name, bexio_document_template: f.bexio_document_template,
      });
      setS(data); setF(data); toast.success(t("Einstellungen gespeichert"));
    } catch { toast.error(t("Speichern fehlgeschlagen")); } finally { setSaving(false); }
  };

  const SaveBar = () => (
    <button onClick={save} disabled={saving} data-testid="settings-save"
      className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
      <Save className="w-4 h-4" /> {t("Alle Einstellungen speichern")}
    </button>
  );

  return (
    <div className="fade-in max-w-3xl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <PageHeader title={t("Rechnungseinstellungen")} subtitle={t("Absendermarke und bexio-Anbindung – ein bexio-Konto für KEWAG & Partner GmbH.")} />
        <SaveBar />
      </div>

      {/* Marke & rechtliche Firma */}
      <Card className="p-6 mb-5">
        <div className="flex items-center gap-2 font-bold mb-4"><Tag className="w-5 h-5 text-[#A3E635]" /> {t("Rechnungsmarke & rechtliche Firma")}</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label={t("Rechnungsmarke")} value={f.brand} onChange={(v) => set("brand", v)} testid="set-brand" />
          <F label={t("Rechtliche Firma")} value={f.legal_company} onChange={(v) => set("legal_company", v)} testid="set-legal" />
          <F label={t("Absenderzeile")} value={f.sender_line} onChange={(v) => set("sender_line", v)} wide testid="set-senderline" />
        </div>
      </Card>

      {/* Absenderdaten */}
      <Card className="p-6 mb-5">
        <div className="flex items-center gap-2 font-bold mb-4"><Building2 className="w-5 h-5 text-[#A3E635]" /> {t("Absenderdaten (KEWAGPage-Rechnungen)")}</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label={t("Strasse")} value={f.sender?.street} onChange={(v) => setSender("street", v)} testid="set-street" />
          <F label={t("PLZ / Ort")} value={f.sender?.zip_city} onChange={(v) => setSender("zip_city", v)} testid="set-zipcity" />
          <F label={t("Land")} value={f.sender?.country} onChange={(v) => setSender("country", v)} testid="set-country" />
          <F label={t("Telefon")} value={f.sender?.phone} onChange={(v) => setSender("phone", v)} testid="set-phone" />
          <F label={t("E-Mail")} value={f.sender?.email} onChange={(v) => setSender("email", v)} testid="set-email" />
          <F label={t("Web")} value={f.sender?.web} onChange={(v) => setSender("web", v)} testid="set-web" />
          <F label={t("UID / MWST")} value={f.sender?.uid} onChange={(v) => setSender("uid", v)} wide testid="set-uid" />
        </div>
      </Card>

      {/* bexio */}
      <Card className="p-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2 font-bold"><Plug className="w-5 h-5 text-[#A3E635]" /> {t("bexio-Anbindung")}</div>
          {s.bexio_connected ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border border-[#A3E635]/40 bg-[#A3E635]/10 text-[#A3E635]" data-testid="bexio-connected-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("Echte bexio-Verbindung aktiv")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5" /> {t("Nicht verbunden")}
            </span>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <F label={t("bexio-Konto")} value={f.bexio_account_name} onChange={(v) => set("bexio_account_name", v)} testid="set-bexio-account" />
          <div>
            <label className="text-xs text-gray-400 mb-1 block">{t("bexio-Dokumentvorlage")}</label>
            <div className="relative">
              <FileText className="w-4 h-4 text-gray-500 absolute left-3 top-2.5" />
              <input className={`${inp} pl-9`} data-testid="set-bexio-template" value={f.bexio_document_template || ""} onChange={(e) => set("bexio_document_template", e.target.value)} />
            </div>
          </div>
        </div>

        {s.bexio_sync_error && (
          <div className="mb-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl p-3" data-testid="bexio-sync-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {s.bexio_sync_error}
          </div>
        )}
        <p className="text-xs text-gray-500 leading-relaxed">
          {t("Der bexio-API-Token wird ausschliesslich serverseitig gespeichert (nie im Frontend). Es gibt nur ein bexio-Konto für KEWAG & Partner GmbH. KEWAGpage.ch ist eine Absendermarke. Bei der Rechnungserstellung sucht/erstellt das System den Kontakt in bexio, erstellt die Rechnung und übernimmt Rechnungsnummer, PDF und Zahlungsstatus aus bexio. KEWAGPage ersetzt keine Buchhaltung.")}
        </p>
      </Card>

      <EmailStatusCard />

      <div className="mt-6"><SaveBar /></div>

      <div className="mt-8">
        <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-3">{t("Mein Konto · Sicherheit")}</h3>
        <TwoFASettings />
      </div>
    </div>
  );
}
